'use client';

/**
 * Global Error Page
 * Handles errors in the root layout (most critical errors)
 *
 * Note: Sentry automatically captures these errors via its error boundary wrapper.
 * We don't manually call Sentry here to avoid prerendering issues.
 */

import { AlertTriangle, RefreshCw } from 'lucide-react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  return (
    <html lang="es">
      <body>
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
          <div className="w-full max-w-2xl">
            <div className="rounded-2xl border border-red-900/50 bg-slate-800/80 p-8 shadow-2xl backdrop-blur-sm">
              {/* Error Icon */}
              <div className="mb-6 flex justify-center">
                <div className="rounded-full bg-red-500/10 p-4">
                  <AlertTriangle className="h-12 w-12 text-red-500" />
                </div>
              </div>

              {/* Error Message */}
              <div className="text-center">
                <h1 className="mb-2 text-2xl font-bold text-white">Error Global de Aplicación</h1>
                <p className="mb-6 text-slate-400">
                  Ha ocurrido un error crítico en la aplicación. Por favor, recarga la página para
                  continuar.
                </p>

                {/* Error Details (Dev Only) */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="mb-6 rounded-lg bg-slate-900/50 p-4 text-left">
                    <p className="mb-2 font-mono text-sm font-semibold text-red-400">
                      {error.message}
                    </p>
                    {error.digest && (
                      <p className="mb-2 font-mono text-xs text-slate-500">
                        Error ID: {error.digest}
                      </p>
                    )}
                    {error.stack && (
                      <pre className="overflow-x-auto text-xs text-slate-400">
                        <code>{error.stack}</code>
                      </pre>
                    )}
                  </div>
                )}

                {/* Action Button */}
                <button
                  onClick={reset}
                  className="bg-brand-lime hover:bg-brand-lime/90 focus:ring-brand-lime/50 inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold text-slate-900 transition-all duration-200 focus:ring-2 focus:outline-none"
                >
                  <RefreshCw className="h-5 w-5" />
                  Recargar Aplicación
                </button>
              </div>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
